import { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Calculator,
  Plus,
  Search,
  Edit,
  Trash2,
  RefreshCw,
  AlertCircle,
  Percent,
  DollarSign,
} from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle, Badge, Button, Input, Modal, Select } from '../../components/ui';
import { api } from '../../lib/api';
import { ENDPOINTS } from '../../config/api';
import { formatCurrency } from '../../lib/utils';

interface FeeRule {
  id: string;
  tenantId: string;
  operationType: string;
  calculationType: 'PERCENTAGE' | 'FIXED' | 'PERCENTAGE_PLUS_FIXED';
  percentageValue: number;
  fixedValue: number;
  minFee: number | null;
  maxFee: number | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const OPERATION_TYPES = [
  { value: 'PIX_RECEIVE', label: 'Pix - Recebimento' },
  { value: 'PIX_SEND', label: 'Pix - Envio' },
  { value: 'TRANSFER', label: 'Transferência Interna' },
  { value: 'BOLETO', label: 'Boleto' },
  { value: 'CARD', label: 'Cartão de Crédito' },
  { value: 'WITHDRAWAL', label: 'Saque' },
  { value: 'MAINTENANCE', label: 'Manutenção' },
];

const CALCULATION_TYPES = [
  { value: 'FIXED', label: 'Taxa Fixa' },
  { value: 'PERCENTAGE', label: 'Percentual' },
  { value: 'PERCENTAGE_PLUS_FIXED', label: 'Percentual + Fixa' },
];

export function AdminFeeRules() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [operationFilter, setOperationFilter] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<FeeRule | null>(null);
  const [formData, setFormData] = useState({
    operationType: 'PIX_RECEIVE',
    calculationType: 'FIXED' as 'PERCENTAGE' | 'FIXED' | 'PERCENTAGE_PLUS_FIXED',
    percentageValue: 0,
    fixedValue: 0,
    minFee: '',
    maxFee: '',
    isActive: true,
  });

  // Fetch fee rules
  const { data: rulesData, isLoading, error, refetch } = useQuery({
    queryKey: ['fee-rules', operationFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (operationFilter) params.append('operationType', operationFilter);
      const url = `${ENDPOINTS.feeRules.list}${params.toString() ? `?${params}` : ''}`;
      const response = await api.get(url);
      // Backend returns { rules: [], total: number }
      const data = response.data;
      return (data.rules || data) as FeeRule[];
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const payload = {
        operationType: data.operationType,
        calculationType: data.calculationType,
        percentageValue: data.percentageValue,
        fixedValue: Math.round(data.fixedValue * 100), // Convert to centavos
        minFee: data.minFee ? Math.round(parseFloat(data.minFee) * 100) : null,
        maxFee: data.maxFee ? Math.round(parseFloat(data.maxFee) * 100) : null,
        isActive: data.isActive,
      };
      const response = await api.post(ENDPOINTS.feeRules.create, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fee-rules'] });
      setIsModalOpen(false);
      resetForm();
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: typeof formData }) => {
      const payload = {
        calculationType: data.calculationType,
        percentageValue: data.percentageValue,
        fixedValue: Math.round(data.fixedValue * 100),
        minFee: data.minFee ? Math.round(parseFloat(data.minFee) * 100) : null,
        maxFee: data.maxFee ? Math.round(parseFloat(data.maxFee) * 100) : null,
        isActive: data.isActive,
      };
      const response = await api.put(ENDPOINTS.feeRules.update(id), payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fee-rules'] });
      setIsModalOpen(false);
      resetForm();
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(ENDPOINTS.feeRules.delete(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fee-rules'] });
    },
  });

  const resetForm = () => {
    setFormData({
      operationType: 'PIX_RECEIVE',
      calculationType: 'FIXED',
      percentageValue: 0,
      fixedValue: 0,
      minFee: '',
      maxFee: '',
      isActive: true,
    });
    setEditingRule(null);
  };

  const handleEdit = (rule: FeeRule) => {
    setEditingRule(rule);
    setFormData({
      operationType: rule.operationType,
      calculationType: rule.calculationType,
      percentageValue: rule.percentageValue,
      fixedValue: rule.fixedValue / 100, // Convert from centavos
      minFee: rule.minFee ? (rule.minFee / 100).toString() : '',
      maxFee: rule.maxFee ? (rule.maxFee / 100).toString() : '',
      isActive: rule.isActive,
    });
    setIsModalOpen(true);
  };

  const handleDelete = (rule: FeeRule) => {
    if (confirm(`Deseja desativar a regra de taxa para "${OPERATION_TYPES.find(t => t.value === rule.operationType)?.label}"?`)) {
      deleteMutation.mutate(rule.id);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingRule) {
      updateMutation.mutate({ id: editingRule.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const rules = rulesData || [];
  const filteredRules = rules.filter(rule =>
    OPERATION_TYPES.find(t => t.value === rule.operationType)?.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatRuleValue = (rule: FeeRule) => {
    if (rule.calculationType === 'FIXED') {
      return formatCurrency(rule.fixedValue);
    }
    if (rule.calculationType === 'PERCENTAGE') {
      return `${rule.percentageValue}%`;
    }
    return `${rule.percentageValue}% + ${formatCurrency(rule.fixedValue)}`;
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <AlertCircle className="w-16 h-16 text-red-400" />
        <h2 className="text-xl font-semibold text-surface-100">Erro ao carregar regras de taxa</h2>
        <Button variant="primary" onClick={() => refetch()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Tentar novamente
        </Button>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Regras de Taxa</h1>
          <p className="text-surface-400">Configure regras de cálculo de taxas por tipo de operação</p>
        </div>
        <Button variant="primary" onClick={() => { resetForm(); setIsModalOpen(true); }}>
          <Plus className="w-4 h-4 mr-2" />
          Nova Regra
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardBody>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Input
                placeholder="Buscar por tipo de operação..."
                leftIcon={<Search className="w-4 h-4" />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Select
              value={operationFilter}
              onChange={(e) => setOperationFilter(e.target.value)}
              options={[
                { value: '', label: 'Todas as operações' },
                ...OPERATION_TYPES,
              ]}
              className="w-56"
            />
            <Button variant="secondary" onClick={() => refetch()} disabled={isLoading}>
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Rules List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-primary-400" />
            Regras Configuradas
            <Badge variant="info">{filteredRules.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="animate-pulse bg-surface-800 rounded h-20" />
              ))}
            </div>
          ) : filteredRules.length === 0 ? (
            <div className="text-center py-12 text-surface-400">
              <Calculator className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nenhuma regra de taxa configurada</p>
            </div>
          ) : (
            <div className="divide-y divide-surface-800">
              {filteredRules.map(rule => (
                <div
                  key={rule.id}
                  className="flex items-center justify-between px-6 py-4 hover:bg-surface-800/30 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      rule.calculationType === 'FIXED' ? 'bg-blue-500/20' :
                      rule.calculationType === 'PERCENTAGE' ? 'bg-green-500/20' :
                      'bg-purple-500/20'
                    }`}>
                      {rule.calculationType === 'FIXED' ? (
                        <DollarSign className="w-6 h-6 text-blue-400" />
                      ) : rule.calculationType === 'PERCENTAGE' ? (
                        <Percent className="w-6 h-6 text-green-400" />
                      ) : (
                        <Calculator className="w-6 h-6 text-purple-400" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-surface-100">
                        {OPERATION_TYPES.find(t => t.value === rule.operationType)?.label || rule.operationType}
                      </p>
                      <p className="text-sm text-surface-500">
                        {CALCULATION_TYPES.find(t => t.value === rule.calculationType)?.label}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="font-medium text-surface-100 text-lg">
                        {formatRuleValue(rule)}
                      </p>
                      {(rule.minFee || rule.maxFee) && (
                        <p className="text-xs text-surface-500">
                          {rule.minFee && `Mín: ${formatCurrency(rule.minFee)}`}
                          {rule.minFee && rule.maxFee && ' | '}
                          {rule.maxFee && `Máx: ${formatCurrency(rule.maxFee)}`}
                        </p>
                      )}
                    </div>
                    <Badge variant={rule.isActive ? 'success' : 'error'}>
                      {rule.isActive ? 'Ativa' : 'Inativa'}
                    </Badge>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(rule)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      {rule.isActive && (
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(rule)}>
                          <Trash2 className="w-4 h-4 text-error" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); resetForm(); }}
        title={editingRule ? 'Editar Regra de Taxa' : 'Nova Regra de Taxa'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Select
            label="Tipo de Operação"
            value={formData.operationType}
            onChange={(e) => setFormData({ ...formData, operationType: e.target.value })}
            options={OPERATION_TYPES}
            required
            disabled={!!editingRule}
          />

          <Select
            label="Tipo de Cálculo"
            value={formData.calculationType}
            onChange={(e) => setFormData({ ...formData, calculationType: e.target.value as typeof formData.calculationType })}
            options={CALCULATION_TYPES}
            required
          />

          {(formData.calculationType === 'PERCENTAGE' || formData.calculationType === 'PERCENTAGE_PLUS_FIXED') && (
            <Input
              label="Percentual (%)"
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={formData.percentageValue}
              onChange={(e) => setFormData({ ...formData, percentageValue: parseFloat(e.target.value) || 0 })}
              required
            />
          )}

          {(formData.calculationType === 'FIXED' || formData.calculationType === 'PERCENTAGE_PLUS_FIXED') && (
            <Input
              label="Valor Fixo (R$)"
              type="number"
              step="0.01"
              min="0"
              value={formData.fixedValue}
              onChange={(e) => setFormData({ ...formData, fixedValue: parseFloat(e.target.value) || 0 })}
              required
            />
          )}

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Taxa Mínima (R$)"
              type="number"
              step="0.01"
              min="0"
              placeholder="Opcional"
              value={formData.minFee}
              onChange={(e) => setFormData({ ...formData, minFee: e.target.value })}
            />
            <Input
              label="Taxa Máxima (R$)"
              type="number"
              step="0.01"
              min="0"
              placeholder="Opcional"
              value={formData.maxFee}
              onChange={(e) => setFormData({ ...formData, maxFee: e.target.value })}
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-primary-500 focus:ring-primary-500"
            />
            <span className="text-sm">Regra ativa</span>
          </label>

          <div className="p-3 bg-surface-800 rounded-lg text-sm text-surface-400">
            <p className="font-medium text-surface-300 mb-1">Exemplo de cálculo:</p>
            {formData.calculationType === 'FIXED' && (
              <p>Para qualquer valor: Taxa = R$ {formData.fixedValue.toFixed(2)}</p>
            )}
            {formData.calculationType === 'PERCENTAGE' && (
              <p>Para R$ 100,00: Taxa = R$ {(100 * formData.percentageValue / 100).toFixed(2)} ({formData.percentageValue}%)</p>
            )}
            {formData.calculationType === 'PERCENTAGE_PLUS_FIXED' && (
              <p>Para R$ 100,00: Taxa = R$ {(100 * formData.percentageValue / 100 + formData.fixedValue).toFixed(2)} ({formData.percentageValue}% + R$ {formData.fixedValue.toFixed(2)})</p>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setIsModalOpen(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              isLoading={createMutation.isPending || updateMutation.isPending}
            >
              {editingRule ? 'Salvar' : 'Criar'}
            </Button>
          </div>
        </form>
      </Modal>
    </motion.div>
  );
}

export default AdminFeeRules;
